import { useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../Auth/context/AuthContext';

/**
 * Hook to call the Drawings API for a given bid.
 */
const useDrawingsApi = () => {
  const { auth } = useContext(AuthContext);
  const token = auth.token;

  /**
   * List processed drawing pages for a bid.
   * @param {string} bidId
   * @param {object} options - { type?: string, page?: number, limit?: number }
   */
  const listPages = async (bidId, options = {}) => {
    const { type, page = 1, limit = 20 } = options;
    const params = { page, limit };
    if (type) params.type = type;
    const resp = await axios.get(`/api/myteconstruction/bids/${bidId}/drawings`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return resp.data; // { items, total, pages, page }
  };
  /**
   * Update metadata for a specific drawing page.
   * @param {string} bidId
   * @param {string} pageId
   * @param {object} updates - fields to update
   */
  const updatePageMeta = async (bidId, pageId, updates) => {
    const resp = await axios.patch(
      `/api/myteconstruction/bids/${bidId}/drawings/${pageId}`,
      updates,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };
  /**
   * Batch approve (clear needsReview) for all pages in a bid.
   * @param {string} bidId
   */
  const batchApprovePages = async (bidId) => {
    const resp = await axios.post(
      `/api/myteconstruction/bids/${bidId}/drawings/batch-approve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };

  /**
   * Get a 5-minute presigned URL to download a specific page PDF.
   * @param {string} bidId
   * @param {string} pageId
   */
  const downloadPage = async (bidId, pageId) => {
    const resp = await axios.get(
      `/api/myteconstruction/bids/${bidId}/drawings/${pageId}/download`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data.url;
  };
  /**
   * List processed drawing pages grouped as a tree by drawingType.
   * @param {string} bidId
   */
  const listTree = async (bidId) => {
    const resp = await axios.get(
      `/api/myteconstruction/bids/${bidId}/drawings/tree`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };

  /**
   * Delete a drawing page.
   * @param {string} bidId
   * @param {string} pageId
   */
  const deletePage = async (bidId, pageId) => {
    const resp = await axios.delete(
      `/api/myteconstruction/bids/${bidId}/drawings/${pageId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };
  /**
   * Upload a binder PDF to start processing.
   * @param {string} bidId
   * @param {File} file
   */
  const uploadBinder = async (bidId, file) => {
    const form = new FormData();
    form.append('file', file);
    const resp = await axios.post(
      `/api/myteconstruction/bids/${bidId}/drawings/bind`,
      form,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    return resp.data; // { binderKey }
  };
  /**
   * Get processing status for drawing jobs.
   * @param {string} bidId
   */
  const getDrawingsStatus = async (bidId) => {
    const resp = await axios.get(
      `/api/myteconstruction/bids/${bidId}/drawings/status`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };
  return { listPages, downloadPage, listTree, updatePageMeta, batchApprovePages, deletePage, uploadBinder, getDrawingsStatus };
};

export default useDrawingsApi;